import {
  InviteCreateDto,
  InviteCreateDtoRoleEnum,
  InviteNestedSelectDto,
  UserCompanyNestedSelectDtoRoleEnum,
  UserNestedSelectFromCompanyDto,
} from '@@types/api.types';
import { RoleSelectDropdown } from '@components/pages/company/components/company-role-select-dropdown.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { CountryFlag } from '@components/shared/country-flag.component';
import React from 'react';

type TeamMemberComponentProps =
  | {
      invite: InviteCreateDto | InviteNestedSelectDto;
      isAdmin?: boolean;
      onChangeRole?: (role: string) => void;
      user?: never;
    }
  | {
      invite?: never;
      isAdmin?: boolean;
      onChangeRole?: (role: string) => void;
      user: UserNestedSelectFromCompanyDto;
    };

const getRoleLabel = (params: {
  invite?: InviteCreateDto | InviteNestedSelectDto;
  user?: UserNestedSelectFromCompanyDto;
}): InviteCreateDtoRoleEnum | undefined => {
  const { invite, user } = params;

  if (invite?.role === InviteCreateDtoRoleEnum.Admin) {
    return InviteCreateDtoRoleEnum.Admin;
  }

  if (invite?.role === InviteCreateDtoRoleEnum.Collaborator) {
    return InviteCreateDtoRoleEnum.Collaborator;
  }

  if (user?.userCompany?.role === UserCompanyNestedSelectDtoRoleEnum.Admin) {
    return InviteCreateDtoRoleEnum.Admin;
  }

  if (user?.userCompany?.role === UserCompanyNestedSelectDtoRoleEnum.Collaborator) {
    return InviteCreateDtoRoleEnum.Collaborator;
  }

  return undefined;
};

export const TeamMemberComponent: React.FC<TeamMemberComponentProps> = (props) => {
  const { invite, isAdmin, onChangeRole, user } = props;

  if (!invite && !user) {
    return null;
  }

  const avatarUrl = user?.avatarUrl;
  const name = invite ? invite.email : user.profile ? `${user.profile.firstName} ${user.profile.lastName}` : '';
  const title = user?.profile?.title;
  const role = getRoleLabel({ invite, user });
  const citizenshipFlag = (
    <CountryFlag
      className="shrink-0"
      country={user?.profile?.country}
      size="sm"
    />
  );
  const teamMemberTitle = invite ? (
    name
  ) : (
    <div className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 truncate">{name}</span>
      {citizenshipFlag}
    </div>
  );

  let subtitle: React.ReactNode;
  let description: React.ReactNode;

  if (invite) {
    subtitle = (
      <div className="bg-[#00BD79]/10 border-[#8fc9b0]/30 border-solid border text-[#193C2D] text-[14px] py-0.5 px-2 rounded-sm w-fit">
        Invited
      </div>
    );
  } else {
    subtitle = title;
    description = user.email;
  }

  return (
    <div className="flex flex-col bg-white rounded-lg w-full border-gray-200 border">
      <div className="p-4 flex gap-4 min-h-15 items-center justify-between">
        <AvatarItem
          imageUrl={avatarUrl}
          altText={`${name} Icon`}
          description={description}
          title={teamMemberTitle}
          subtitle={subtitle}
        />
        <div className="shrink-0">{!onChangeRole && <div>{role}</div>}</div>
        {onChangeRole && (
          <div className="w-36">
            <RoleSelectDropdown
              onChange={onChangeRole}
              isAdmin={isAdmin}
              currentRole={role}
              showRemove={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
